import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { DeclareMethod } from '/imports/methods';
import { Doc } from '/imports/types';
import { ProductionMeter } from '/imports/api/productionMeters';
import { IntervalGranularity, numYearsBetween } from '/imports/utils';

export interface ProductionInterval extends Doc {
  meterId: string;
  startTime: Date;
  endTime: Date;
  production: number;
  revenue: number;
  yearZeroRevenue: number;
  toGridKwh?: number;
}

export const ProductionIntervals = new Mongo.Collection<ProductionInterval>('productionIntervals');

export const pullProductionIntervals = DeclareMethod<{ meterIds: string[], from: Date, to: Date, sync?: boolean }, void>('pullProductionIntervals');
export const exportProductionIntervals = DeclareMethod<{ meterIds: string[], from: Date, to: Date, useUtc?: boolean }, string>('exportProductionIntervals');
/**
 * One bar of a production chart. Raw intervals and rollup rows both arrive in this shape, so the chart does
 * not care which collection answered — only `granularity`, which it labels the axis with.
 */
export interface ProductionIntervalBucket {
  startTime: Date;
  endTime: Date;
  production: number;
}

export interface ProductionIntervalSeries {
  granularity: IntervalGranularity;
  intervals: ProductionIntervalBucket[];
}

export const getProductionIntervals = DeclareMethod<{ meterId: string, from: Date, to: Date }, ProductionIntervalSeries>('getProductionIntervals');
export const productionIntervalGapsReport = DeclareMethod<{ meterIds?: string[], from: Date, to: Date }, { meter: ProductionMeter, gaps: Date[][] }[]>('productionIntervalGapsReport');

/**
 * Computes PPA revenue (escalated per commissioning age) for an interval.
 * @param interval - interval with startTime/endTime/production
 * @param meter - meter supplying commissioning date and PPA terms
 * @returns interval augmented with revenue and yearZeroRevenue
 */
export const enrichInterval = <T extends { startTime: Date, endTime: Date, production: number }>(interval: T, meter: ProductionMeter) => {
  const { startTime, production } = interval;
  const { commissioningDate, ppaRate, ppaEscalator } = { commissioningDate: new Date(), ppaRate: 0, ppaEscalator: 0, ...meter };
  const numYears = commissioningDate ? numYearsBetween(startTime, commissioningDate) : 0;
  const yearZeroRevenue = production * ppaRate;
  const revenue = yearZeroRevenue * (1 + numYears * (ppaEscalator / 100));
  return { ...interval, revenue, yearZeroRevenue };
};

/**
 * Upserts enriched intervals for a meter, clamping negative values to zero.
 * @param intervals - intervals to ingest
 * @param meter - owning meter
 */
export const ingestIntervals = async <T extends { startTime: Date, endTime: Date, production: number, toGridKwh?: number }>(intervals: T[], meter: ProductionMeter) => {
  const meterId = meter._id;
  for (const interval of intervals) {
    const { startTime, endTime, production, revenue, yearZeroRevenue, toGridKwh } = enrichInterval(interval, meter);
    const existing = await ProductionIntervals.findOneAsync({ meterId, startTime });
    const _production = production > 0 ? production : 0;
    const _revenue = revenue > 0 ? revenue : 0;
    const _yearZeroRevenue = yearZeroRevenue > 0 ? yearZeroRevenue : 0;
    const $set: Partial<ProductionInterval> = { startTime, endTime, production: _production, revenue: _revenue, yearZeroRevenue: _yearZeroRevenue };
    // Grid-export energy, only supplied by providers that report it (FOMware). Set conditionally so the
    // other providers' rows keep no toGridKwh key at all rather than gaining an empty one.
    if (typeof toGridKwh === 'number') $set.toGridKwh = toGridKwh > 0 ? toGridKwh : 0;
    if (existing) await ProductionIntervals.updateAsync(existing._id, { $set });
    else await ProductionIntervals.insertAsync({ meterId, ...$set } as ProductionInterval);
  }
};

/**
 * Returns the end time of the meter's latest interval, or its commissioning date.
 * @param meter - meter to inspect
 * @returns most recent interval endTime or commissioningDate
 */
export const lastProductionIntervalTime = async (meter: ProductionMeter) => {
  const meterId = meter._id;
  const intervals = await ProductionIntervals.find({ meterId }, { sort: { startTime: -1 }, limit: 1 }).fetchAsync();
  const interval = intervals[0];
  return interval?.endTime || meter.commissioningDate;
};

/**
 * Finds missing-data gaps between consecutive 15-minute intervals over a range.
 * @param intervals - intervals sorted by startTime
 * @param fromDate - range start
 * @param toDate - range end
 * @returns array of [start, end] gap pairs
 */
export const findProductionIntervalGaps = async (intervals: ProductionInterval[], fromDate: Date, toDate: Date) => {
  if (intervals.length === 0) return [[fromDate, toDate]];
  const gaps = [];
  let cursor = intervals[0];
  for (const interval of intervals.slice(1)) {
    const nextStartTime = new Date(cursor.startTime.getTime());
    nextStartTime.setMinutes(nextStartTime.getMinutes() + 15);
    if (interval.startTime.getTime() !== nextStartTime.getTime()) gaps.push([cursor.endTime, interval.startTime]);
    cursor = interval;
  }
  const lastInterval = intervals[intervals.length - 1];
  if (lastInterval.endTime.getTime() < toDate.getTime()) gaps.push(lastInterval.endTime, toDate);
  return gaps as [Date, Date][];
};

if (Meteor.isServer) {
  ProductionIntervals.createIndex({ meterId: 1, startTime: 1 });
  ProductionIntervals.createIndex({ startTime: 1 });
}

export interface CachedProductionInterval extends Doc {
  meterId: string;
  startDate: string;
  endDate: string;
  production: number;
  revenue: number;
  yearZeroRevenue: number;
}
export const ProductionIntervalsDaily = new Mongo.Collection<CachedProductionInterval>('productionIntervalsDaily');
export const ProductionIntervalsMonthly = new Mongo.Collection<CachedProductionInterval>('productionIntervalsMonthly');
export const ProductionIntervalsYtd = new Mongo.Collection<CachedProductionInterval>('productionIntervalsYtd');

export const cacheAggregateProductionIntervals = DeclareMethod<{ meterIds?: string[], startDate: string }, void>('cacheAggregateProductionIntervals');

if (Meteor.isServer) {
  ProductionIntervalsDaily.createIndex({ meterId: 1, startDate: 1 }, { unique: true });
  ProductionIntervalsDaily.createIndex({ startDate: 1 });
  ProductionIntervalsMonthly.createIndex({ meterId: 1, startDate: 1 }, { unique: true });
  ProductionIntervalsMonthly.createIndex({ startDate: 1 });
  ProductionIntervalsYtd.createIndex({ meterId: 1 }, { unique: true });
}